import type { FunctionComponent } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useFormikContext } from "formik";

import { PriorityAlert } from "components/Shared/PriorityAlert";
import { useReportNavigation } from "../../ReportNavigation";
import { ExpandableAlert } from "components/Shared";
import { useIncidentReportContext } from "../../IncidentReportContextProvider";

import "./Review.scss";

export const Review: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { errors } = useFormikContext();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const { allMenus } = useReportNavigation();
  const { affirmations, updateAffirmation, isAU } = useIncidentReportContext();
  const hasErrors = Object.keys(errors ?? {}).length > 0;

  return translationsReady && (
    <div className="review_and_submit">
      <h2>{t('review_and_submit')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          {t('review_and_submit_instructions')}
        </Grid>
        <Grid item sm={12}>
          {hasErrors ? (
            <ExpandableAlert 
              priority="critical"
              summary={(
                <>
                  <p>{t('errors')}</p>
                  <div>{t('errors_instruction')}</div>
                </>
              )}
              defaultExpanded
            >
              <ul className="incident_report_errors">
                {
                  allMenus.map(menuItem => {
                    const hasError = Object.keys(errors).some(errorSection => menuItem.labelKey === errorSection);
                    return hasError ? <li className="error_row" key={menuItem.labelKey}>
                      <span>{t(menuItem.labelKey)}</span>
                      <Link to={menuItem?.url || '#'}>{t('review')}</Link>
                    </li> : null
                  })
                }
              </ul>
            </ExpandableAlert>
          ) : (
            <PriorityAlert priority="high">
              <div className="reminder_alert_content">
                <p className="reminder_alert_header">
                  <strong>{t('reminder')}:</strong><a href={t('timesheet_template_url')} target="_blank">{t('download_template')}</a>
                </p>
                <p className="reminder_alert_body" dangerouslySetInnerHTML={{ __html: t('timesheet_reminder_message') }} />
              </div>
            </PriorityAlert>
          )}
        </Grid>
        <Grid item sm={12}>
          <FormControl fullWidth>
            <FormControlLabel
              className="global__paragraph"
              label={t('confirm_submission_correct')}
              control={<Checkbox checked={affirmations.submissionIsCorrect || isReadonly} disabled={isReadonly} onChange={(e) => updateAffirmation({ submissionIsCorrect: e.target.checked })} />}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormControlLabel
              className="global__paragraph"
              label={t('confirm_timesheet_sent')}
              control={<Checkbox checked={affirmations.timesheetSent || isReadonly} disabled={isReadonly} onChange={(e) => updateAffirmation({ timesheetSent: e.target.checked })} />}
            />
          </FormControl>
          {isAU && (
            <FormControl fullWidth>
              <FormControlLabel
                className="global__paragraph"
                label={t('confirm_follow_ups_finalised')}
                control={<Checkbox checked={affirmations.followUpsFinalised || isReadonly} disabled={isReadonly} onChange={(e) => updateAffirmation({ followUpsFinalised: e.target.checked })} />}
              />
            </FormControl>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
